/**
 * Lightweight toast notifications + confirm dialog primitives.
 *
 * Why hand-rolled instead of `sonner` / `react-hot-toast` — keeps the bundle
 * lean and matches our Tailwind theme variables. The store is a single
 * module-level subject; any component can call `pushToast()` / `confirmAsync()`.
 *
 * Usage:
 *
 *     pushToast({ kind: "success", text: "Сохранено" })
 *     pushToast({ kind: "error", text: "Не удалось", detail: err.message })
 *
 *     const yes = await confirmAsync({
 *       title: "Удалить прокси?",
 *       text: "Действие необратимо.",
 *       confirmKind: "danger",
 *     })
 *     if (yes) await del.mutateAsync(...)
 */

// File exports both the React component (Toaster) and the imperative API
// (pushToast / dismissToast / confirmAsync) backed by a module-level store.
// Vite's fast-refresh rule prefers component-only files, but splitting this
// pair into two modules would just couple them via imports while breaking
// the obvious co-location. Disable at file level.
/* eslint-disable react-refresh/only-export-components */

import {
  type ReactNode,
  useEffect,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Check, Info, TriangleAlert, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/cn";

export type ToastKind = "info" | "success" | "warning" | "error";

export interface ToastInput {
  kind?: ToastKind;
  text: string;
  detail?: string;
  /** Auto-dismiss after this many ms (default 5000; 0 = sticky). */
  durationMs?: number;
  /**
   * Optional inline action (e.g. "Отменить" for undo-toasts). Clicking it
   * runs the callback and dismisses the toast. Additive — existing call
   * sites are unaffected.
   */
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface Toast extends ToastInput {
  id: number;
}

interface Confirm {
  id: number;
  title: string;
  text?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmKind?: "default" | "danger";
  resolve: (yes: boolean) => void;
}

// ---------------------------------------------------------------------------
// Module-level store
// ---------------------------------------------------------------------------

type Listener = () => void;

let _toasts: Toast[] = [];
let _confirm: Confirm | null = null;
let _seq = 1;
const _listeners: Set<Listener> = new Set();

function emit(): void {
  for (const fn of _listeners) {
    fn();
  }
}

let _lastErrorAt = 0;

/** True when an error toast went up within the last `ms`. The global
 *  mutation fallback uses it to stay quiet when the caller has just reported
 *  the failure in its own words. */
export function errorToastShownRecently(ms = 400): boolean {
  return Date.now() - _lastErrorAt < ms;
}

export function pushToast(input: ToastInput): number {
  const id = _seq++;
  const t: Toast = { id, durationMs: 5000, kind: "info", ...input };
  if (t.kind === "error") _lastErrorAt = Date.now();
  _toasts = [..._toasts, t];
  emit();
  if (t.durationMs && t.durationMs > 0) {
    setTimeout(() => dismissToast(id), t.durationMs);
  }
  return id;
}

export function dismissToast(id: number): void {
  _toasts = _toasts.filter((t) => t.id !== id);
  emit();
}

export function confirmAsync(input: {
  title: string;
  text?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmKind?: "default" | "danger";
}): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    if (_confirm !== null) {
      // Only one modal at a time — newer one rejects the older.
      _confirm.resolve(false);
    }
    _confirm = { id: _seq++, ...input, resolve };
    emit();
  });
}

function resolveConfirm(yes: boolean): void {
  const c = _confirm;
  if (c === null) return;
  _confirm = null;
  c.resolve(yes);
  emit();
}

// ---------------------------------------------------------------------------
// React subscription hook
// ---------------------------------------------------------------------------

function useStore(): { toasts: Toast[]; confirm: Confirm | null } {
  const [, force] = useState(0);
  useEffect(() => {
    const fn = () => force((n) => n + 1);
    _listeners.add(fn);
    return () => {
      _listeners.delete(fn);
    };
  }, []);
  return { toasts: _toasts, confirm: _confirm };
}

// ---------------------------------------------------------------------------
// Renderer — mount once at the app root
// ---------------------------------------------------------------------------

export function Toaster(): ReactNode {
  const { toasts, confirm } = useStore();
  const { t } = useTranslation();

  return (
    <>
      {/* Toasts — bottom-right stack on desktop, top on mobile */}
      <div
        className={cn(
          "fixed z-[60] flex flex-col gap-2 pointer-events-none",
          "bottom-4 right-4 left-4 sm:left-auto sm:max-w-sm",
        )}
        aria-live="polite"
        // Drawers look for this: a press on a toast is not a press "outside".
        data-toaster=""
      >
        {toasts.map((t) => (
          <ToastCard key={t.id} t={t} />
        ))}
      </div>

      {/* Confirm modal */}
      {confirm !== null && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-bg-primary/80 p-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-sm space-y-4 rounded-lg border border-border bg-bg-secondary p-6 shadow-2xl">
            <h3 className="text-lg font-semibold">{confirm.title}</h3>
            {confirm.text && (
              <p className="text-sm text-fg-secondary">{confirm.text}</p>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => resolveConfirm(false)}
                autoFocus
              >
                {confirm.cancelLabel ?? t("common.cancel")}
              </Button>
              <Button
                type="button"
                variant={confirm.confirmKind === "danger" ? "danger" : "default"}
                onClick={() => resolveConfirm(true)}
              >
                {confirm.confirmLabel ?? t("common.confirm")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ToastCard({ t }: { t: Toast }): ReactNode {
  const colors = {
    info: "border-fg-muted bg-bg-elevated text-fg-primary",
    success: "border-success/40 bg-success/15 text-fg-primary",
    warning: "border-warning/40 bg-warning/15 text-fg-primary",
    error: "border-danger/40 bg-danger/15 text-fg-primary",
  } as const;
  const Icon = {
    info: Info,
    success: Check,
    warning: TriangleAlert,
    error: X,
  }[t.kind ?? "info"];
  return (
    // Opaque base under the tint: the tint alone is 15% colour, and whatever
    // the toast covered stayed readable through it.
    <div className="pointer-events-auto rounded-lg bg-bg-secondary shadow-lg" role="status">
      <div
        className={cn(
          "rounded-lg border px-4 py-3",
          "flex items-start gap-3 text-sm",
          colors[t.kind ?? "info"],
        )}
      >
        <Icon aria-hidden="true" size={16} strokeWidth={2} className="mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-medium">{t.text}</p>
          {t.detail && (
            <p className="text-xs text-fg-secondary mt-0.5 break-words">{t.detail}</p>
          )}
        </div>
        {t.action && (
          <button
            type="button"
            onClick={() => {
              t.action?.onClick();
              dismissToast(t.id);
            }}
            className="shrink-0 self-center rounded-md px-2 py-1 text-xs font-semibold text-accent hover:bg-accent/15"
          >
            {t.action.label}
          </button>
        )}
        <button
          type="button"
          onClick={() => dismissToast(t.id)}
          className="text-fg-muted hover:text-fg-primary leading-none"
          aria-label="dismiss"
        >
          <X aria-hidden="true" size={16} strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}
